/**
 * a2ui SESSION smart-wrapper APIs — the bridge between a resolved a2ui component
 * node and the EXISTING viewIr session-block kit + host handlers.
 *
 * The 14 session customs (hero_media, stat_grid, chip_list, …) share their names
 * with the viewIr session-block types, so a resolved a2ui node maps straight onto
 * a `{type, data}` node the existing kit already draws (`ViewIRComposition`). Host
 * verbs a component fires (`imageTap`, `activated`, `saveRequested`, …) route to
 * the SAME `onCardAction` / `onFollowUp` channel SessionBlockCard uses, so both
 * surfaces reach the host identically. Nothing new on the wire.
 */

import { followUpAction } from './catalog.js';

/** The rendererOnly host verbs the session surface exposes (DEC-8). */
export const SESSION_HOST_FUNCTIONS = [
  'viewImage', 'playVideo', 'saveMedia', 'openSession', 'reloadSession', 'share', 'openUrl',
];

/** Blocks whose `tapped` opens a picture full-screen (mirrors SessionBlockCard). */
const OPENS_VIEWER = new Set(['hero_media', 'before_after', 'image_gallery', 'shopping_list', 'table']);
const OPENS_FILE = new Set(['file_card']);

/** Map an action-bar host op enum → the reported action key. */
const ACTION_HOST_OP = { openSession: 'openSession', reloadData: 'reloadData', macroShare: 'share' };

/**
 * Build the `emit(signal, payload)` a resolved custom block uses. Identical host
 * translation to `SessionBlockCard` so the session-block kit behaves the same
 * whether it was reached via native-ir or a2ui.
 */
export function makeSessionEmit({ type, cardId, onCardAction, onFollowUp, onOpenImage }) {
  return (signal, payload = {}) => {
    const send = (action, extra = {}) => onCardAction?.({ action, cardId, ...extra });
    const followUp = (text) => {
      const words = typeof text === 'string' ? text.trim() : '';
      if (words) onFollowUp?.(words);
    };

    if (signal === 'tapped' && OPENS_VIEWER.has(type)) {
      if (payload.src) onOpenImage?.(payload.src);
      send('open_image', { url: payload.src });
      return;
    }
    if (signal === 'tapped' && OPENS_FILE.has(type) && payload.src) {
      send('open_url', { url: payload.src });
      return;
    }
    if (signal === 'saveRequested') {
      send('download_file', { url: payload.url, ...(payload.filename ? { filename: payload.filename } : {}) });
      return;
    }
    if (signal === 'activated') {
      if (payload.url) { send('open_url', { url: payload.url }); return; }
      const op = ACTION_HOST_OP[payload.action];
      if (op) {
        send(`block_${op}`, { index: payload.index, label: payload.label, block_action: payload.action });
        if (op === 'openSession') followUp(payload.label);
        return;
      }
      send('block_chip', payload);
      followUp(payload.value);
      return;
    }
    send(`block_${signal}`, payload);
  };
}

/**
 * A resolved a2ui custom node → the viewIr session-block node the kit draws.
 * `IRNode` resolves a block's fields from `node.props`, so the resolved a2ui
 * props become the composition node's `props` (mirrors SessionBlockCard's
 * `renderableNode`, which returns `{type, props:data}`).
 *
 * The a2ui model may author list ITEMS in a richer object shape than the
 * session-block renderer reads (a `numbered_steps.steps` of `{title,description}`
 * where the block wants strings; an `icon_list.items` of `{icon,label,description}`
 * where the block reads `{name,detail}`). Normalize per type so every catalog
 * item shape renders and string items still work.
 */
export function customToBlockNode(node) {
  return { type: node.component, props: normalizeCustomItems(node.component, node.props || {}) };
}

function str(v) { return v == null ? '' : (typeof v === 'string' ? v : String(v)); }
function firstOf(...vals) { for (const v of vals) if (v != null) return v; return undefined; }
function withIcon(icon, text) {
  const t = str(text);
  return icon && typeof icon === 'string' && !/^\s*$/.test(icon) ? `${icon} ${t}`.trim() : t;
}

/** Flatten a step (string or `{title,description}`-ish object) to the block's string. */
function stepToString(s) {
  if (typeof s === 'string') return s;
  if (s && typeof s === 'object') {
    const title = str(s.title || s.label || s.name || s.text || s.step);
    const detail = str(s.description || s.detail || s.body || s.subtitle);
    return detail ? `${title} — ${detail}`.trim().replace(/^—\s*/, '') : title;
  }
  return str(s);
}

export function normalizeCustomItems(type, props) {
  if (!props || typeof props !== 'object') return props || {};
  const p = { ...props };
  if (type === 'numbered_steps' && Array.isArray(p.steps)) {
    p.steps = p.steps.map(stepToString).filter(Boolean);
  } else if (type === 'icon_list' && Array.isArray(p.items)) {
    // block reads {name, detail, image}; the model authors {icon, label|title, description|subtitle}
    p.items = p.items.map((it) => (typeof it === 'string'
      ? { name: it }
      : {
        ...it,
        name: withIcon(it.icon, firstOf(it.name, it.label, it.title, it.text)),
        detail: firstOf(it.detail, it.description, it.subtitle),
      }));
  } else if (type === 'stat_grid' && Array.isArray(p.items)) {
    // block reads {label, value, unit}; fold a leading emoji `icon` into the label,
    // and accept `title` as the label alias
    p.items = p.items.map((it) => (it && typeof it === 'object'
      ? { ...it, label: withIcon(it.icon, firstOf(it.label, it.title)) } : it));
  } else if (type === 'shopping_list' && Array.isArray(p.items)) {
    // block reads {name, brand, quantity, image, price}; accept label/title as name
    p.items = p.items.map((it) => (it && typeof it === 'object' && it.name == null
      ? { ...it, name: it.label != null ? it.label : it.title } : it));
  } else if (type === 'chip_list' && Array.isArray(p.chips)) {
    // block maps chips with str(); an object chip renders "[object Object]"
    p.chips = p.chips.map((c) => (typeof c === 'string'
      ? c : (c && typeof c === 'object' ? str(c.label != null ? c.label : (c.value != null ? c.value : c.text)) : str(c))));
  }
  return p;
}

/**
 * Extract the follow_up intent from a resolved Button/chip node's `action`.
 * a2ui Action = `{ event: { name, userMessage?, context? } }`; we also accept a
 * flattened `{ name, userMessage, context }` our own converter may emit.
 */
export function actionIntent(action) {
  if (!action || typeof action !== 'object') return null;
  const ev = action.event && typeof action.event === 'object' ? action.event : action;
  const name = ev.name || 'follow_up';
  return { name, userMessage: ev.userMessage, context: ev.context && typeof ev.context === 'object' ? ev.context : {} };
}

/** Build the standard exec-return envelope for a follow_up from a component tap. */
export function buildFollowUpEnvelope({ surfaceId, sourceComponentId, userMessage, context }) {
  return followUpAction({ surfaceId, sourceComponentId, userMessage, context });
}
